import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ArrowRightLeft, Send, Clock, CheckCircle, XCircle, Plus, X, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { useSyncRefresh, notifySyncUpdate } from '../hooks/useSyncRefresh';

import API from '../utils/api';

var STATUS_STYLES = {
  pending: { color: '#EAB308', bg: '#EAB30815', label: 'Pending', icon: Clock },
  approved: { color: '#10B981', bg: '#10B98115', label: 'Approved', icon: CheckCircle },
  denied: { color: '#EF4444', bg: '#EF444415', label: 'Denied', icon: XCircle },
};

function ChangeRequests() {
  var [data, setData] = useState({ sent: [], received: [] });
  var [loading, setLoading] = useState(true);
  var [showForm, setShowForm] = useState(false);
  var [submitting, setSubmitting] = useState(false);
  var [tab, setTab] = useState('received');
  var [form, setForm] = useState({ coparent_email: '', request_type: 'swap', original_date: '', proposed_date: '', reason: '' });
  var [actionLoading, setActionLoading] = useState('');

  function loadData() {
    axios.get(API + '/change-requests', { withCredentials: true })
      .then(function (res) { setData(res.data); })
      .catch(function () {})
      .finally(function () { setLoading(false); });
  }

  useEffect(function () { loadData(); }, []);
  useSyncRefresh(useCallback(function() { loadData(); }, []));

  function handleSubmit(e) {
    e.preventDefault();
    if (!form.coparent_email || !form.original_date || submitting) return;
    setSubmitting(true);
    axios.post(API + '/change-requests', form, { withCredentials: true })
      .then(function () {
        setShowForm(false);
        setForm({ coparent_email: '', request_type: 'swap', original_date: '', proposed_date: '', reason: '' });
        loadData();
      })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Failed to send request'); })
      .finally(function () { setSubmitting(false); });
  }

  function handleAction(id, action) {
    setActionLoading(id);
    axios.patch(API + '/change-requests/' + id, { action: action }, { withCredentials: true })
      .then(function () { loadData(); })
      .catch(function (err) { alert(err.response ? err.response.data.detail : 'Action failed'); })
      .finally(function () { setActionLoading(''); });
  }

  function handleCancel(id) {
    if (!window.confirm('Cancel this change request?')) return;
    setActionLoading(id);
    axios.delete(API + '/change-requests/' + id, { withCredentials: true })
      .then(function () { loadData(); })
      .catch(function () {})
      .finally(function () { setActionLoading(''); });
  }

  var pendingReceived = data.received.filter(function (r) { return r.status === 'pending'; }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="change-requests-loading">
        <div className="w-8 h-8 border-2 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="change-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-[#10B981]" />
          Calendar Change Requests
        </h2>
        <button onClick={function () { setShowForm(!showForm); }}
          className="px-4 py-2 bg-[#10B981] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2"
          data-testid="new-request-btn">
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? 'Cancel' : 'New Request'}
        </button>
      </div>

      {/* New Request Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="card-surface p-5 space-y-4" data-testid="change-request-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-white/40 mb-1">Co-Parent Email</label>
              <input type="email" required value={form.coparent_email}
                onChange={function (e) { setForm(Object.assign({}, form, { coparent_email: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5"
                placeholder="coparent@email.com"
                data-testid="cr-email-input" />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Request Type</label>
              <select value={form.request_type}
                onChange={function (e) { setForm(Object.assign({}, form, { request_type: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5"
                data-testid="cr-type-select">
                <option value="swap">Date Swap</option>
                <option value="give">Give My Day</option>
                <option value="take">Request Extra Day</option>
                <option value="reschedule">Reschedule</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Original Date</label>
              <input type="date" required value={form.original_date}
                onChange={function (e) { setForm(Object.assign({}, form, { original_date: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5"
                data-testid="cr-original-date" />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Proposed Date (optional)</label>
              <input type="date" value={form.proposed_date}
                onChange={function (e) { setForm(Object.assign({}, form, { proposed_date: e.target.value })); }}
                className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5"
                data-testid="cr-proposed-date" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">Reason</label>
            <textarea value={form.reason}
              onChange={function (e) { setForm(Object.assign({}, form, { reason: e.target.value })); }}
              className="w-full bg-white/5 border border-white/10 text-white text-sm rounded-lg px-3 py-2.5 h-20 resize-none"
              placeholder="Explain why you need this change..."
              data-testid="cr-reason" />
          </div>
          <button type="submit" disabled={submitting}
            className="px-5 py-2.5 bg-[#10B981] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-40 transition-opacity flex items-center gap-2"
            data-testid="cr-submit-btn">
            <Send className="w-4 h-4" />
            {submitting ? 'Sending...' : 'Send Request'}
          </button>
        </form>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button onClick={function () { setTab('received'); }}
          className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
            (tab === 'received' ? 'bg-[#2D2560]/20 text-[#2D2560] border border-[#2D2560]/30' : 'bg-white/5 text-white/50 border border-white/10')}
          data-testid="tab-received">
          <ArrowDownLeft className="w-3.5 h-3.5 inline mr-1.5" />
          Received ({data.received.length})
          {pendingReceived > 0 && (
            <span className="ml-2 w-5 h-5 inline-flex items-center justify-center rounded-full bg-[#EAB308] text-black text-xs font-bold">{pendingReceived}</span>
          )}
        </button>
        <button onClick={function () { setTab('sent'); }}
          className={'px-4 py-2 rounded-lg text-sm font-medium transition-colors ' +
            (tab === 'sent' ? 'bg-[#5A4E9E]/20 text-[#5A4E9E] border border-[#5A4E9E]/30' : 'bg-white/5 text-white/50 border border-white/10')}
          data-testid="tab-sent">
          <ArrowUpRight className="w-3.5 h-3.5 inline mr-1.5" />
          Sent ({data.sent.length})
        </button>
      </div>

      {/* Request List */}
      <div className="space-y-3" data-testid="request-list">
        {(tab === 'received' ? data.received : data.sent).length === 0 ? (
          <div className="card-surface p-8 text-center">
            <ArrowRightLeft className="w-8 h-8 text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">No {tab} requests yet</p>
          </div>
        ) : (
          (tab === 'received' ? data.received : data.sent).map(function (req) {
            var st = STATUS_STYLES[req.status] || STATUS_STYLES.pending;
            var StIcon = st.icon;
            var isLoading = actionLoading === req.id;
            return (
              <div key={req.id} className="card-surface p-4" data-testid={'request-' + req.id}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: st.bg, color: st.color }}>
                        <StIcon className="w-3 h-3 inline mr-1" />
                        {st.label}
                      </span>
                      <span className="text-xs px-2 py-0.5 bg-white/5 rounded-full text-white/50 capitalize">{req.request_type}</span>
                    </div>
                    <p className="text-sm text-white/80 mt-1">
                      {tab === 'received'
                        ? <span><strong className="text-white">{req.from_user_name}</strong> requests a change</span>
                        : <span>Sent to <strong className="text-white">{req.to_user_name}</strong></span>}
                    </p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-white/40">
                      <span>Original: <strong className="text-white/60">{req.original_date}</strong></span>
                      {req.proposed_date && <span>Proposed: <strong className="text-white/60">{req.proposed_date}</strong></span>}
                    </div>
                    {req.reason && <p className="text-xs text-white/30 mt-1 italic">"{req.reason}"</p>}
                  </div>

                  {/* Actions */}
                  <div className="flex-shrink-0 flex gap-2">
                    {tab === 'received' && req.status === 'pending' && (
                      <>
                        <button onClick={function () { handleAction(req.id, 'approve'); }} disabled={isLoading}
                          className="px-3 py-1.5 bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30 rounded-lg text-xs font-medium hover:bg-[#10B981]/30 disabled:opacity-40"
                          data-testid={'approve-' + req.id}>
                          <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Approve
                        </button>
                        <button onClick={function () { handleAction(req.id, 'deny'); }} disabled={isLoading}
                          className="px-3 py-1.5 bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-lg text-xs font-medium hover:bg-[#EF4444]/30 disabled:opacity-40"
                          data-testid={'deny-' + req.id}>
                          <XCircle className="w-3.5 h-3.5 inline mr-1" /> Deny
                        </button>
                      </>
                    )}
                    {tab === 'sent' && req.status === 'pending' && null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default ChangeRequests;
